import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { IconProvider } from '@/components/ui';
import { DialogField, DialogVariable } from '@/lib/stores/dialogStore';
import { Input } from '@/components/ui/InputDefault';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { getCachedTypes, TypeElement } from '@/lib/stores/typesStore';
import { getCachedObjects } from '@/lib/stores/objectsStore';
import { useFieldEditingHandlers, FieldTextInput } from './BaseFieldEditor';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export interface EndFieldEditorProps {
  field: DialogField;
  fieldIndex: number;
  sectionIndex: number;
  sectionIdentifiers: string[];
  sections?: { section_identifier: string; name: string }[];
  currentSectionFields?: DialogField[];
  currentDialogId?: string;
  dialogTranslatable: boolean;
  speakerMode: 'none' | 'type' | 'free-text' | 'object';
  speakerTypeId?: string | null;
  speakerTemplateIds?: string[];
  dialogVariables?: DialogVariable[];
  onUpdateField: (sectionIndex: number, fieldId: string, updates: Partial<DialogField>) => void;
  onRemoveField: (sectionIndex: number, fieldId: string) => void;
  gameId?: string;
  t: any;
}

/**
 * EndFieldEditor handles 'end-text' and 'end-dialog' field types
 * These are terminal nodes with no navigation options
 * end-dialog includes speaker selection like regular dialog
 */
export const EndFieldEditor = React.memo(function EndFieldEditor({
  field,
  fieldIndex,
  sectionIndex,
  sectionIdentifiers,
  sections,
  currentSectionFields,
  currentDialogId,
  dialogTranslatable,
  speakerMode,
  speakerTypeId,
  speakerTemplateIds,
  dialogVariables,
  onUpdateField,
  onRemoveField,
  gameId,
  t,
}: EndFieldEditorProps) {
  const { selectedGame } = useSimplifiedUserData();
  const effectiveGameId = gameId || selectedGame?.id;

  const { locaKey, displayText, sourceText, isTranslationMode, handleTextChange, handleTextBlur } = useFieldEditingHandlers(
    field, effectiveGameId, onUpdateField, sectionIndex
  );

  // Get data for speaker selection (for end-dialog)
  const types = effectiveGameId ? getCachedTypes(effectiveGameId) : [];
  const objects = effectiveGameId ? getCachedObjects(effectiveGameId) : [];

  // Get type elements if speaker mode is 'type'
  const selectedType = speakerMode === 'type' && speakerTypeId
    ? types.find(t => t.id === speakerTypeId)
    : null;
  const typeElements = selectedType?.data || [];

  // Get objects from selected templates if speaker mode is 'object'
  const availableObjects = useMemo(() => {
    if (speakerMode === 'object' && speakerTemplateIds && speakerTemplateIds.length > 0) {
      if (import.meta.env.DEV) {
        console.log('[Dialog Speaker Filter - End Field] Debug Info:', {
          speakerMode,
          selectedTemplateIds: speakerTemplateIds,
          totalObjects: objects.length,
          objectsWithBlueprintId: objects.filter(obj => obj.blueprint_id).length,
          uniqueBlueprintIds: [...new Set(objects.map(obj => obj.blueprint_id).filter(Boolean))],
          objectTypes: [...new Set(objects.map(obj => obj.type).filter(Boolean))],
        });
      }
      
      // Filter objects: must have blueprint_id matching selected templates
      // AND should be glossary/library type (not document type) for speakers
      const filtered = objects.filter(obj => 
        obj.blueprint_id && 
        speakerTemplateIds.includes(obj.blueprint_id) &&
        (obj.type === 'glossary' || obj.type === 'Library Object' || !obj.type)
      );
      
      if (import.meta.env.DEV) {
        console.log('[Dialog Speaker Filter - End Field] Filtered objects:', filtered.length, filtered.map(obj => ({ id: obj.id, name: obj.name, type: obj.type, blueprint_id: obj.blueprint_id })));
      }
      
      return filtered;
    }
    return [];
  }, [speakerMode, speakerTemplateIds, objects]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };


  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 p-2 border border-gray-200 rounded-sm bg-white"
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 pt-2"
        type="button"
      >
        <IconProvider iconstring="fa-grip-vertical" size={14} />
      </button>

      {/* Field Content */}
      <div className="flex-1 items-center justify-center space-y-2">
        {/* Speaker Selection (only for end-dialog with speaker mode enabled) */}
        {speakerMode !== 'none' && field.type === 'end-dialog' && (
          <div className="flex items-start gap-2 pb-1">
            <Label className="text-sm pt-2 min-w-[80px]">
              Character:
            </Label>
            <div className="flex-1">
              {speakerMode === 'free-text' && (
                <Input
                  value={field.speaker || ''}
                  onChange={(e) => onUpdateField(sectionIndex, field.id, { speaker: e.target.value })}
                  placeholder="Enter character name..."
                  className="h-8 text-sm"
                />
              )}
              {speakerMode === 'type' && (
                <Select
                  value={field.speaker || ''}
                  onValueChange={(value) => onUpdateField(sectionIndex, field.id, { speaker: value })}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select character..." />
                  </SelectTrigger>
                  <SelectContent>
                    {typeElements.length === 0 ? (
                      <SelectItem value="__empty" disabled>
                        No elements available
                      </SelectItem>
                    ) : (
                      typeElements.map((element: TypeElement) => (
                        <SelectItem key={element.id} value={element.id}>
                          {element.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
              {speakerMode === 'object' && (
                <Select
                  value={field.speaker || ''}
                  onValueChange={(value) => onUpdateField(sectionIndex, field.id, { speaker: value })}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select character..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableObjects.length === 0 ? (
                      <SelectItem value="__empty" disabled>
                        No objects available
                      </SelectItem>
                    ) : (
                      availableObjects.map((obj) => (
                        <SelectItem key={obj.id} value={obj.id}>
                          {obj.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        )}

        {/* Field Text with Translation Icon */}
        <FieldTextInput
          field={field}
          displayText={displayText}
          locaKey={locaKey}
          sourceText={sourceText}
          isTranslationMode={isTranslationMode}
          dialogTranslatable={dialogTranslatable}
          onTextChange={handleTextChange}
          onTextBlur={handleTextBlur}
          onTypeChange={(type) => onUpdateField(sectionIndex, field.id, { type })}
          placeholder={
            field.type === 'end-dialog'
              ? 'Enter ending dialog...'
              : 'Enter ending text...'
          }
          gameId={effectiveGameId}
        />

        {/* Info message about terminal fields */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded">
          <IconProvider iconstring="fa-circle-info" size={12} />
          <span>This is a terminal field - the dialog ends here.</span>
        </div>
      </div>

      {/* Field Type & Buttons */}
      <div className="flex items-center gap-1">
        {/* Remove Field Button */}
        <Button
          variant="halfghost"
          size="sm"
          onClick={() => onRemoveField(sectionIndex, field.id)}
          className="px-2 text-xs w-8 h-8"
          type="button"
        >
          <IconProvider
            iconstring="fa-xmark"
            size={14}
            color="currentColor"
            className="hover:opacity-80 transition-colors"
          />
        </Button>
      </div>
    </div>
  );
});
